/**
 * SettingsLayout — shell for all settings pages.
 *
 * `md` and up (two columns, unchanged):
 *   LEFT:  sticky sidebar at w-64 (256px), full height, scrollable
 *          internally. Contains SettingsNav (grouped).
 *   RIGHT: scrollable content area; fixed max-w-3xl so the layout
 *          never reflows when the active subroute changes. The sidebar
 *          width is fixed via CSS so navigating between settings pages
 *          never shifts column widths.
 *
 * Below `md` (phones):
 *   The sidebar is not rendered. A full-width section switcher sits at the
 *   top of the content showing the current group + section; tapping it
 *   opens the grouped SettingsNav in a bottom sheet (same overlay pattern
 *   as the AppShell mobile drawer: fixed backdrop, z-40, ESC + backdrop
 *   close, focus moved into the panel). Choosing a section navigates and
 *   closes the sheet. The AppShell mobile header is still visible above,
 *   so no "Settings" title is repeated here.
 *
 * Page-level content (the right pane) is provided via React Router's
 * <Outlet /> so each settings page renders as a child route without
 * knowing about the shell.
 */
import { useSpaceNoun } from '@/lib/useSpaceNoun';
import { ChevronsUpDown, Settings2, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { SettingsNav, findSettingsSection, settingsLabelFor } from './SettingsNav';

export function SettingsLayout() {
  return (
    <div className="flex min-h-[100dvh]">
      {/* Sidebar — md and up only */}
      <aside
        className="sticky top-0 hidden h-[100dvh] shrink-0 overflow-y-auto border-r border-border bg-background md:block"
        style={{ width: '256px' }}
        aria-label="Settings navigation"
      >
        <div className="px-4 py-6">
          <p className="mb-4 px-2.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
            Settings
          </p>
          <SettingsNav />
        </div>
      </aside>

      {/* Content area */}
      <main className="min-w-0 flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-3xl px-4 py-6 md:px-8 md:py-10">
          <MobileSectionSwitcher />
          <Outlet />
        </div>
      </main>
    </div>
  );
}

/**
 * Compact section switcher rendered above the page content below `md`.
 * Shows "Group · Section" with the section's icon; opens the grouped nav
 * in a bottom sheet.
 */
function MobileSectionSwitcher() {
  const { pathname } = useLocation();
  const noun = useSpaceNoun();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  const current = findSettingsSection(pathname);
  const Icon = current?.entry.Icon ?? Settings2;
  const sectionLabel = current
    ? settingsLabelFor(current.entry.to, current.entry.label, noun)
    : 'Settings';

  // Close on route change (a link inside the sheet was chosen).
  // biome-ignore lint/correctness/useExhaustiveDependencies: pathname is the trigger, not a value we read
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  // ESC closes; focus moves into the panel on open and back to the
  // trigger on close.
  useEffect(() => {
    if (!open) return;
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      triggerRef.current?.focus();
    };
  }, [open]);

  return (
    <div className="mb-5 md:hidden">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`Settings section: ${sectionLabel}. Change section`}
        className="flex min-h-11 w-full items-center gap-3 rounded-xl border border-border bg-surface px-3 py-2 text-left press"
      >
        <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary">
          <Icon className="size-4" strokeWidth={1.75} />
        </span>
        <span className="min-w-0 flex-1">
          {current && (
            <span className="block text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground/70">
              {current.group.label}
            </span>
          )}
          <span className="block truncate text-sm font-medium">{sectionLabel}</span>
        </span>
        <ChevronsUpDown className="size-4 shrink-0 text-muted-foreground" strokeWidth={1.75} />
      </button>

      {open && (
        <div
          className="fixed inset-0 z-40 md:hidden"
          role="dialog"
          aria-modal="true"
          aria-label="Settings sections"
        >
          <button
            type="button"
            aria-label="Dismiss section picker"
            className="absolute inset-0 bg-black/40"
            onClick={() => setOpen(false)}
          />
          <div className="absolute inset-x-0 bottom-0 flex max-h-[85dvh] flex-col rounded-t-2xl bg-background shadow-2xl">
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
              <span className="text-sm font-medium">Jump to section</span>
              <button
                ref={closeRef}
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close section picker"
                className="grid size-9 place-items-center rounded-lg hover:bg-secondary press"
              >
                <X className="size-4" strokeWidth={1.75} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-3 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
              <SettingsNav variant="sheet" onNavigate={() => setOpen(false)} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
